/**
 * Umrechnung zwischen `<input type="date">` / `<input type="time">` und dem
 * ISO-Zeitstempel, den die API speichert (SPEC 9.1).
 *
 * Eigenes Modul statt zweier Hilfsfunktionen im Sheet: hier steckt die
 * Zeitzonen-Arithmetik, und die ist der Teil, der sich still verrechnet.
 * Gerechnet wird durchgaengig in Ortszeit — `Europe/Berlin` laut SPEC 16.
 */

/**
 * Ohne Uhrzeit gilt eine Karte bis zum Ende des Tages als faellig.
 *
 * Nicht Mitternacht: eine Aufgabe fuer „heute" waere sonst um 00:01 bereits
 * ueberfaellig und der Faelligkeits-Chip den ganzen Tag rot.
 */
const END_OF_DAY = { hours: 23, minutes: 59 };

/** Raster des Uhrzeit-Feldes in Minuten (`step={900}` im Kartendetail). */
export const TIME_STEP_MINUTES = 15;

/**
 * Uhrzeit auf das Viertelstunden-Raster bringen.
 *
 * Das Feld rastet bereits (`step`), aber das gilt nur fuer die Bedienung: ein
 * Wert aus der Zeit vor dieser Aenderung — oder aus der Schnellerfassung —
 * kann krumm sein. Gerundet wird deshalb an BEIDEN Enden, beim Anzeigen wie
 * beim Speichern; sonst stuende im Feld etwas anderes als in der Datenbank.
 *
 * Gerundet wird zur naechsten Viertelstunde, aber **nie ueber den Tag
 * hinaus**: aus 23:53 wuerde sonst 00:00 des Folgetages, und die Aufgabe
 * spraenge einen Tag weiter, ohne dass jemand das Datum angefasst haette.
 */
export function roundToQuarterHour(hours: number, minutes: number): { hours: number; minutes: number } {
  const total = Math.round((hours * 60 + minutes) / TIME_STEP_MINUTES) * TIME_STEP_MINUTES;
  const capped = Math.min(total, 23 * 60 + 45);
  return { hours: Math.floor(capped / 60), minutes: capped % 60 };
}

export interface DueInput {
  /** `yyyy-MM-dd`, leer wenn kein Datum gesetzt ist. */
  date: string;
  /** `HH:mm`, leer wenn die Karte ganztaegig ist. */
  time: string;
}

export function toDueInput(value: string | null, hasTime: boolean): DueInput {
  if (!value) return { date: '', time: '' };

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: '', time: '' };

  const pad = (part: number) => `${part}`.padStart(2, '0');
  const rounded = roundToQuarterHour(date.getHours(), date.getMinutes());

  return {
    date: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    // Die Uhrzeit wird nur gezeigt, wenn sie auch gemeint war. Sonst staende
    // bei jeder ganztaegigen Karte „23:59" im Feld, und wer das einmal
    // speichert, hat daraus versehentlich eine Uhrzeit gemacht.
    time: hasTime ? `${pad(rounded.hours)}:${pad(rounded.minutes)}` : '',
  };
}

export function fromDueInput({ date, time }: DueInput): {
  dueAt: string | null;
  dueHasTime: boolean;
} {
  if (!date) return { dueAt: null, dueHasTime: false };

  const [year, month, day] = date.split('-').map(Number);
  if (!year || !month || !day) return { dueAt: null, dueHasTime: false };

  const [hours, minutes] = time
    ? time.split(':').map(Number)
    : [END_OF_DAY.hours, END_OF_DAY.minutes];

  // Eine Uhrzeit ohne Minuten gibt es nicht; `time` liefert immer HH:mm.
  if (time && (hours === undefined || Number.isNaN(hours) || minutes === undefined)) {
    return { dueAt: null, dueHasTime: false };
  }

  // Ohne Uhrzeit bleibt die Tagesende-Marke, wie sie ist — 23:59 ist hier
  // kein Wunsch des Nutzers, sondern das Kennzeichen fuer „ganztaegig".
  const final = time
    ? roundToQuarterHour(hours ?? 0, minutes ?? 0)
    : { hours: END_OF_DAY.hours, minutes: END_OF_DAY.minutes };

  const at = new Date(year, month - 1, day, final.hours, final.minutes, 0, 0);
  return { dueAt: at.toISOString(), dueHasTime: Boolean(time) };
}
